import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { SelectModule } from 'primeng/select';
import { finalize, of, switchMap } from 'rxjs';
import { DriverPayTripView } from '../../accounts/workforce-cash/workforce-cash.models';
import { WorkforceCashService } from '../../accounts/workforce-cash/workforce-cash.service';
import { ShipmentAccountsPostingPreview, ShipmentService } from '../shipment.service';
import { GpsTrackingService } from '../../fleet/gps-tracking/gps-tracking.service';
import { GpsTrackingSource } from '../../fleet/gps-tracking/gps-tracking.model';
import { Shipment } from '../models/shipment';

@Component({
    selector: 'app-shipment-accounts-review-dialog', standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, DialogModule, InputGroupModule, InputGroupAddonModule,
        InputNumberModule, InputTextModule, IftaLabelModule, SelectModule],
    templateUrl: './shipment-accounts-review-dialog.component.html',
    styleUrls: ['../../shared/party-quick-create/party-quick-create-dialog.component.css',
        './shipment-accounts-review-dialog.component.css']
})
export class ShipmentAccountsReviewDialogComponent implements OnChanges {
    @Input() visible = false;
    @Input() shipmentId?: number;
    @Input() shipmentDetail?: Shipment;
    @Input() posting = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() confirmed = new EventEmitter<void>();
    preview?: ShipmentAccountsPostingPreview;
    trips: DriverPayTripView[] = [];
    gpsSources: GpsTrackingSource[] = [];
    loading = false;
    busy = false;
    error = '';
    readonly sources = [
        { label: 'Shipment route distance', value: 'PLANNED_ROUTE' },
        { label: 'GPS device odometer', value: 'GPS_ODOMETER' },
        { label: 'Start/end odometer photos', value: 'PHOTO_ODOMETER' }
    ];
    drafts: Record<number, { distanceSource: string; plannedDistanceKm?: number; gpsSourceKey?: string;
        correctedStartKm?: number; correctedEndKm?: number; reason?: string }> = {};

    linesFor(...categories: string[]): ShipmentAccountsPostingPreview['lines'] {
        const internalDeliveries = new Set((this.preview?.internalCostLines || []).map(line => line.deliveryReference));
        return (this.preview?.lines || []).filter(line => categories.includes(line.category)
            && (line.category !== 'DELIVERY_TRANSPORT' || !internalDeliveries.has(line.reference)));
    }
    categoryLabel(category: string): string {
        return ({ DELIVERY_TRANSPORT: 'Delivery transport', CASH_REQUEST: 'Delivery cash request',
            WORKER_PAY: 'Worker earning', DRIVER_PAY_TRIP: 'Distance pay approval' } as Record<string, string>)[category]
            || this.statusLabel(category);
    }
    statusLabel(status?: string): string {
        if (!status) return 'Pending';
        return status.replace(/^DRIVER_JOB_/, '').replace(/^JOB_DRIVER_/, '').replace(/_/g, ' ').toLowerCase()
            .replace(/^./, first => first.toUpperCase());
    }
    detailLabel(detail?: string): string {
        return (detail || '').replace(/\bDRIVER_TRIP\b/g, 'Driver trip pay')
            .replace(/\bDRIVER_DISTANCE\b/g, 'Driver distance pay').replace(/_/g, ' ');
    }
    tripDeliveryReferences(view: DriverPayTripView): string {
        const references = view.deliveries.map(link => this.shipmentDetail?.deliveries
            ?.find(delivery => delivery.deliveryId === link.deliveryId)?.deliveryNumber)
            .filter((reference): reference is string => !!reference);
        return references.length ? references.join(' · ') : 'Delivery reference unavailable';
    }
    tripCrew(view: DriverPayTripView): string {
        const delivery = this.shipmentDetail?.deliveries?.find(item => view.deliveries
            .some(link => link.deliveryId === item.deliveryId)
            && item.deliveryDriver?.id === view.trip.driverId
            && item.deliveryVehicle?.vehicleId === view.trip.vehicleId);
        const driver = delivery?.deliveryDriver?.legalName || delivery?.deliveryDriver?.name;
        const vehicle = delivery?.deliveryVehicle?.plateNumber || delivery?.deliveryVehicle?.registrationNumber;
        return [driver, vehicle].filter(Boolean).join(' · ') || 'Assigned crew details unavailable';
    }

    constructor(private shipment: ShipmentService, private workforce: WorkforceCashService,
        private gps: GpsTrackingService, private cdr: ChangeDetectorRef) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.visible && (changes['visible'] || changes['shipmentId'])) this.refresh();
    }
    close(): void { this.visibleChange.emit(false); }
    refresh(): void {
        if (!this.shipmentId) return;
        this.loading = true; this.error = '';
        this.shipment.accountsPostingPreview(this.shipmentId).pipe(finalize(() => {
            this.loading = false;
            this.cdr.markForCheck();
        })).subscribe({
            next: preview => {
                this.preview = preview;
                this.cdr.markForCheck();
                this.gps.getTrackingSources(preview.owningCompanyId)
                    .pipe(finalize(() => this.cdr.markForCheck())).subscribe({
                    next: sources => this.gpsSources = (sources || []).filter(source => source.active !== false),
                    error: () => this.gpsSources = []
                });
                this.workforce.driverPayTrips(preview.owningCompanyId, preview.shipmentId)
                    .pipe(finalize(() => this.cdr.markForCheck())).subscribe({
                    next: trips => { this.trips = trips || []; for (const { trip } of this.trips)
                        this.drafts[trip.driverPayTripId] ??= { distanceSource: trip.selectedDistanceSource || trip.intendedDistanceSource, reason: trip.reason }; },
                    error: error => this.error = error?.error?.message || 'Driver pay trips could not be loaded.'
                });
            },
            error: error => this.error = error?.error?.message || 'Accounts preview could not be loaded.'
        });
    }
    capture(id: number, boundary: 'START' | 'END', event?: Event): void {
        if (!this.preview || this.busy) return;
        const file = (event?.target as HTMLInputElement | undefined)?.files?.[0];
        if (file && !['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            this.error = 'Choose a JPEG, PNG, or WebP dashboard photo.'; return;
        }
        if (file && file.size > 9_000_000) { this.error = 'The odometer image must be below 9 MB.'; return; }
        const send = (imageBase64?: string) => {
            this.busy = true; this.error = '';
            this.workforce.captureDriverPayTrip(id, this.preview!.owningCompanyId, boundary,
                { imageBase64, mimeType: file?.type, place: boundary === 'START' ? 'Before pickup' : 'Approved parking' })
                .pipe(finalize(() => { this.busy = false; this.cdr.markForCheck(); })).subscribe({
                    next: () => this.refresh(), error: error => this.error = error?.error?.message || 'Trip evidence could not be saved.'
                });
        };
        if (!file) { send(); return; }
        const reader = new FileReader();
        reader.onload = () => send(String(reader.result).split(',')[1]);
        reader.onerror = () => { this.error = 'The image could not be read.'; this.cdr.markForCheck(); };
        reader.readAsDataURL(file);
    }
    reviewAndApprove(id: number): void {
        if (!this.preview || this.busy) return;
        const value = this.drafts[id];
        this.busy = true; this.error = '';
        this.workforce.reviewDriverPayTrip(id, this.preview.owningCompanyId, value).pipe(
            switchMap(trip => trip.status === 'READY'
                ? this.workforce.approveDriverPayTrip(id, this.preview!.owningCompanyId) : of(trip)),
            finalize(() => { this.busy = false; this.cdr.markForCheck(); })).subscribe({
                next: () => this.refresh(), error: error => this.error = error?.error?.message || 'Driver pay review failed.'
            });
    }
}
